<!-- ── Toolbar: search + action buttons ───────────────────────────────── -->
<div class="knowledge-base__toolbar">
  <ui-form-field
    class="knowledge-base__search"
    type="text"
    [prefixIcon]="'search'"
    [placeholder]="'knowledge_base.search_placeholder' | translate"
    [(ngModel)]="searchQuery"
    (ngModelChange)="onSearchChange($event)"
  ></ui-form-field>
  <!-- Bulk actions for the current selection live in the floating
       ui-bulk-actions-bar at the bottom of the table, not here. -->
  <div class="knowledge-base__toolbar-actions">
    <ui-button [label]="'knowledge_base.ask_ai' | translate" type="primary-text-outlined" icon="sparkle" (click)="onAskAi()"></ui-button>
    <ui-button
      *appHasPermission="{ module: 'knowledge_base', action: 'create' }"
      [label]="'knowledge_base.upload_file' | translate"
      type="primary"
      icon="upload"
      [isLoading]="(loading$ | async)?.upload ?? false"
      (click)="onUploadFile()"
    ></ui-button>
  </div>
</div>

<!-- ── Type filter pills, with the index stats on the same row ────────── -->
<div class="knowledge-base__type-filters" *ngIf="typeFilters$ | async as filters">
  <span class="knowledge-base__type-filters-label">{{ "knowledge_base.filter_by_type" | translate }}:</span>
  <div class="knowledge-base__type-filters-scroll" #typeFiltersScroll (mousedown)="onFiltersScrollMouseDown($event)">
    <button
      *ngFor="let filter of filters"
      class="knowledge-base__type-filter-tab"
      [class.knowledge-base__type-filter-tab--active]="(activeTypeFilter$ | async) === filter.type"
      (click)="onTypeFilterChange(filter.type)"
    >
      {{ filter.labelKey | translate }}
      <span class="knowledge-base__type-filter-count" *ngIf="filter.count > 0">{{ filter.count }}</span>
    </button>
  </div>

  <!-- Read-only counts: they belong beside the filters that change them, not on
       a row of their own above the toolbar. -->
  <div class="knowledge-base__stats" *ngIf="stats$ | async as stats">
    <span class="knowledge-base__stats-item">
      <ui-svg-icon svgIcon="knowledge-base" size="medium"></ui-svg-icon>
      {{ "knowledge_base.indexed" | translate }}: <strong>{{ stats.indexedCount }}</strong>
    </span>
    <span class="knowledge-base__stats-item" *ngIf="stats.lastUploadDate">
      <ui-svg-icon svgIcon="calendar" size="medium"></ui-svg-icon>
      {{ "knowledge_base.last_upload_date" | translate }}: <strong>{{ stats.lastUploadDate | adaptToUserUtcOffset | date: DATE_TIME_FORMAT }}</strong>
    </span>
  </div>
</div>

<!-- ── Document table ─────────────────────────────────────────────────── -->
<div class="knowledge-base__table" [class.knowledge-base__table--with-bulk-bar]="selection.hasSelection">
  <!-- Table header -->
  <div class="knowledge-base__table-header">
    <div class="knowledge-base__table-cell knowledge-base__table-cell--checkbox" (click)="$event.stopPropagation()">
      <ui-checkbox
        [checked]="selection.isAllSelected"
        [indeterminate]="selection.isPartialSelected"
        [ariaLabel]="'common.select_all' | translate"
        (changed)="onToggleSelectAll($event.checked)"
      ></ui-checkbox>
    </div>
    <div class="knowledge-base__table-cell knowledge-base__table-cell--name">
      {{ "knowledge_base.table.file_name" | translate }}
      <div class="knowledge-base__table-cell-sort-actions">
        <span class="arrow-up" [class.active]="activeSortMode == 'asc' && activeSortKey == 'name'" (click)="onSortChange('name', 'asc')"></span>
        <span class="arrow-down" [class.active]="activeSortMode == 'desc' && activeSortKey == 'name'" (click)="onSortChange('name', 'desc')"></span>
      </div>
    </div>
    <div class="knowledge-base__table-cell knowledge-base__table-cell--type">
      {{ "knowledge_base.table.type" | translate }}
      <div class="knowledge-base__table-cell-sort-actions">
        <span class="arrow-up" [class.active]="activeSortMode == 'asc' && activeSortKey == 'type'" (click)="onSortChange('type', 'asc')"></span>
        <span class="arrow-down" [class.active]="activeSortMode == 'desc' && activeSortKey == 'type'" (click)="onSortChange('type', 'desc')"></span>
      </div>
    </div>
    <div class="knowledge-base__table-cell knowledge-base__table-cell--status">
      {{ "knowledge_base.table.status" | translate }}
      <div class="knowledge-base__table-cell-sort-actions">
        <span class="arrow-up" [class.active]="activeSortMode == 'asc' && activeSortKey == 'status'" (click)="onSortChange('status', 'asc')"></span>
        <span
          class="arrow-down"
          [class.active]="activeSortMode == 'desc' && activeSortKey == 'status'"
          (click)="onSortChange('status', 'desc')"
        ></span>
      </div>
    </div>
    <div class="knowledge-base__table-cell knowledge-base__table-cell--date">
      {{ "knowledge_base.table.upload_date" | translate }}
      <div class="knowledge-base__table-cell-sort-actions">
        <span
          class="arrow-up"
          [class.active]="activeSortMode == 'asc' && activeSortKey == 'upload_date'"
          (click)="onSortChange('upload_date', 'asc')"
        ></span>
        <span
          class="arrow-down"
          [class.active]="activeSortMode == 'desc' && activeSortKey == 'upload_date'"
          (click)="onSortChange('upload_date', 'desc')"
        ></span>
      </div>
    </div>
  </div>

  <!-- Scrollable body -->
  <div class="knowledge-base__table-body">
    <!-- Loading state -->
    <div class="knowledge-base__loading" *ngIf="(loading$ | async)?.documents">
      <div class="knowledge-base__loading-spinner"></div>
    </div>

    <!-- Document rows -->
    <ng-container *ngIf="!(loading$ | async)?.documents">
      <app-file-row
        *ngFor="let doc of filteredDocuments$ | async; trackBy: trackByDocumentId"
        [document]="doc"
        [selected]="selection.isSelected(doc.id)"
        (selectionChange)="onToggleSelectDocument(doc.id, $event)"
      ></app-file-row>

      <!-- Empty state -->
      <div class="knowledge-base__empty" *ngIf="(filteredDocuments$ | async)?.length === 0">
        <p>{{ "knowledge_base.empty_state" | translate }}</p>
      </div>
    </ng-container>
  </div>

  <!-- ───────────────────────────────────────────────────────────────────── -->
  <!-- Bulk actions for the current selection. Floats centred at the bottom of -->
  <!-- the table so selecting rows never reflows the toolbar above it.         -->
  <!-- ───────────────────────────────────────────────────────────────────── -->
  <ui-bulk-actions-bar *ngIf="selection.hasSelection" [count]="selection.count" (cleared)="selection.clear()">
    <button class="ui-bulk-action" [disabled]="downloadLoading" (click)="onDownloadSelected()">
      <ui-icon name="Download" size="medium"></ui-icon>
      <span>{{ "knowledge_base.download" | translate }}</span>
    </button>
    <button
      *appHasPermission="{ module: 'knowledge_base', action: 'delete' }"
      class="ui-bulk-action ui-bulk-action--danger"
      (click)="onDeleteSelected()"
    >
      <ui-icon name="Trash2" size="medium"></ui-icon>
      <span>{{ "common.delete" | translate }}</span>
    </button>
  </ui-bulk-actions-bar>
  <!-- ── END BULK ACTIONS BAR ───────────────────────────────────────────── -->
</div>
